import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { labAPI } from '../../services/api';
import { useNotify } from '../../context/NotifyContext';
import TableToolbar from '../TableToolbar';
import Modal from '../Modal';

const STAFF_ROLES = [
  { value: 'driver', label: 'Driver' },
  { value: 'phlebotomist', label: 'Phlebotomist (staff)' },
  { value: 'lab_tech', label: 'Lab tech' },
  { value: 'courier', label: 'Courier' },
  { value: 'admin', label: 'Admin' },
  { value: 'other', label: 'Other' }
];

const TASK_STATUSES = [
  { value: 'todo', label: 'To do' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' }
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' }
];

const ROLE_TAGS = [
  { value: 'driver', label: 'Driver' },
  { value: 'phlebotomist', label: 'Phlebotomist' },
  { value: 'lab_tech', label: 'Lab tech' },
  { value: 'courier', label: 'Courier' },
  { value: 'admin', label: 'Admin' },
  { value: 'other', label: 'Other' }
];

const COL_STORAGE = 'labTaskTrackerColumns';

const defaultColumns = () => ({
  priority: true,
  roleTag: true,
  dueDate: true,
  assignee: true,
  status: true
});

function loadColumnPrefs() {
  try {
    const raw = localStorage.getItem(COL_STORAGE);
    if (!raw) return defaultColumns();
    return { ...defaultColumns(), ...JSON.parse(raw) };
  } catch {
    return defaultColumns();
  }
}

function saveColumnPrefs(cols) {
  try {
    localStorage.setItem(COL_STORAGE, JSON.stringify(cols));
  } catch {
    /* ignore */
  }
}

const LabTaskTracker = () => {
  const { showSuccess, showError } = useNotify();
  const [team, setTeam] = useState({ employees: [], phlebotomists: [] });
  const [tasks, setTasks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [taskSearch, setTaskSearch] = useState('');
  const [taskStatusFilter, setTaskStatusFilter] = useState('');
  const [columns, setColumns] = useState(loadColumnPrefs);
  const [showColSettings, setShowColSettings] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'normal',
    roleTag: 'other',
    dueDate: '',
    assignee: ''
  });
  const [editTask, setEditTask] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [teamRes, taskRes] = await Promise.all([labAPI.getLabTeam(), labAPI.getLabTasks()]);
      setTeam(teamRes.data || { employees: [], phlebotomists: [] });
      setTasks(taskRes.data?.tasks || []);
    } catch (e) {
      console.error(e);
      showError('Could not load team or tasks');
    }
  }, [showError]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const assigneeOptions = useMemo(() => {
    const em = (team.employees || []).map((u) => ({
      id: u._id,
      label: `${u.name} (${STAFF_ROLES.find((r) => r.value === (u.staffRole || 'other'))?.label || 'Staff'})`,
      group: 'Lab staff'
    }));
    const ph = (team.phlebotomists || []).map((u) => ({
      id: u._id,
      label: `${u.name} (Phlebotomist)`,
      group: 'Phlebotomists'
    }));
    return [...em, ...ph];
  }, [team]);

  const toggleColumn = (key) => {
    setColumns((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      saveColumnPrefs(next);
      return next;
    });
  };

  const onPatchStaffRole = async (userId, staffRole) => {
    setBusy(true);
    try {
      await labAPI.patchEmployeeStaffRole(userId, { staffRole });
      setTeam((t) => ({
        ...t,
        employees: (t.employees || []).map((e) => (e._id === userId ? { ...e, staffRole } : e))
      }));
      showSuccess('Role updated');
    } catch (err) {
      showError(err.response?.data?.message || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const onCreateTask = async (e) => {
    e.preventDefault();
    if (!newTask.title.trim()) return;
    setBusy(true);
    try {
      await labAPI.createLabTask({
        title: newTask.title.trim(),
        description: newTask.description,
        priority: newTask.priority,
        roleTag: newTask.roleTag,
        dueDate: newTask.dueDate || undefined,
        assignee: newTask.assignee || undefined
      });
      setNewTask({ title: '', description: '', priority: 'normal', roleTag: 'other', dueDate: '', assignee: '' });
      await loadAll();
      showSuccess('Task created');
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to create task');
    } finally {
      setBusy(false);
    }
  };

  const onUpdateTask = async (taskId, payload) => {
    setBusy(true);
    try {
      const { data: body } = await labAPI.updateLabTask(taskId, payload);
      setTasks((prev) => prev.map((t) => (t._id === taskId ? body.task : t)));
      setEditTask(null);
      showSuccess('Task updated');
    } catch (err) {
      showError(err.response?.data?.message || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const onDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    setBusy(true);
    try {
      await labAPI.deleteLabTask(taskId);
      setTasks((prev) => prev.filter((t) => t._id !== taskId));
      showSuccess('Task deleted');
    } catch (err) {
      showError(err.response?.data?.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || new Date(b.createdAt) - new Date(a.createdAt));
  }, [tasks]);

  const moveTask = async (taskId, dir) => {
    const next = [...sortedTasks];
    const index = next.findIndex((t) => t._id === taskId);
    const j = index + dir;
    if (index < 0 || j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    const orderedIds = next.map((t) => t._id);
    setBusy(true);
    try {
      const { data } = await labAPI.reorderLabTasks({ orderedIds });
      setTasks(data.tasks || next);
    } catch (err) {
      showError(err.response?.data?.message || 'Reorder failed');
      await loadAll();
    } finally {
      setBusy(false);
    }
  };

  const filteredTasks = useMemo(() => {
    let rows = tasks;
    if (taskStatusFilter) rows = rows.filter((t) => t.status === taskStatusFilter);
    const q = taskSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((t) => {
      const a = (t.assignee?.name || '').toLowerCase();
      return (
        (t.title || '').toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q) ||
        a.includes(q)
      );
    });
  }, [tasks, taskSearch, taskStatusFilter]);

  return (
    <div>
      <div className="card" style={{ marginBottom: '24px' }}>
        <h2 className="card-title" style={{ marginBottom: '8px' }}>Team &amp; roles</h2>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>
          Lab staff accounts linked to your lab. Set each person&apos;s role for clearer task assignment. Independent phlebotomists appear in the assignee list for field work.
        </p>
        {!team.employees?.length && (
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            No lab employee accounts yet. Create <code>lab_employee</code> users in admin / seed and set their <code>labId</code> to this lab.
          </p>
        )}
        {!!team.employees?.length && (
          <div className="table-wrapper" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                </tr>
              </thead>
              <tbody>
                {team.employees.map((emp) => (
                  <tr key={emp._id}>
                    <td style={{ fontWeight: 500 }}>{emp.name}</td>
                    <td style={{ fontSize: '0.875rem' }}>{emp.email}</td>
                    <td style={{ minWidth: '200px' }}>
                      <select
                        className="form-select"
                        disabled={busy}
                        value={emp.staffRole || 'other'}
                        onChange={(e) => onPatchStaffRole(emp._id, e.target.value)}
                      >
                        {STAFF_ROLES.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <h2 className="card-title" style={{ marginBottom: '12px' }}>Create task</h2>
        <form onSubmit={onCreateTask} style={{ display: 'grid', gap: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Title</label>
              <input
                className="form-input"
                value={newTask.title}
                onChange={(e) => setNewTask((n) => ({ ...n, title: e.target.value }))}
                required
                placeholder="e.g. Deliver supplies to Main St clinic"
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Assign to</label>
              <select
                className="form-select"
                value={newTask.assignee}
                onChange={(e) => setNewTask((n) => ({ ...n, assignee: e.target.value }))}
              >
                <option value="">Unassigned</option>
                {assigneeOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Due</label>
              <input
                type="date"
                className="form-input"
                value={newTask.dueDate}
                onChange={(e) => setNewTask((n) => ({ ...n, dueDate: e.target.value }))}
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Priority</label>
              <select
                className="form-select"
                value={newTask.priority}
                onChange={(e) => setNewTask((n) => ({ ...n, priority: e.target.value }))}
              >
                {PRIORITIES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Work type</label>
              <select
                className="form-select"
                value={newTask.roleTag}
                onChange={(e) => setNewTask((n) => ({ ...n, roleTag: e.target.value }))}
              >
                {ROLE_TAGS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Details</label>
            <textarea
              className="form-textarea"
              rows={2}
              value={newTask.description}
              onChange={(e) => setNewTask((n) => ({ ...n, description: e.target.value }))}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            Add task
          </button>
        </form>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
          <h2 className="card-title" style={{ margin: 0 }}>Task list</h2>
          <button type="button" className="btn btn-outline" style={{ fontSize: '0.8125rem' }} onClick={() => setShowColSettings(true)}>
            Column visibility
          </button>
        </div>
        <TableToolbar
          search={taskSearch}
          onSearchChange={setTaskSearch}
          placeholder="Search title, details, assignee…"
          resultCount={filteredTasks.length}
          totalCount={tasks.length}
        >
          <select className="form-select" value={taskStatusFilter} onChange={(e) => setTaskStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {TASK_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </TableToolbar>
        <div className="table-wrapper" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: '72px' }}>Order</th>
                <th>Task</th>
                {columns.priority && <th>Priority</th>}
                {columns.roleTag && <th>Type</th>}
                {columns.assignee && <th>Assignee</th>}
                {columns.dueDate && <th>Due</th>}
                {columns.status && <th>Status</th>}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map((t) => {
                const pos = sortedTasks.findIndex((x) => x._id === t._id);
                return (
                <tr key={t._id}>
                  <td>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button type="button" className="btn btn-outline" style={{ padding: '2px 8px', fontSize: '0.75rem' }} disabled={busy || pos <= 0} onClick={() => moveTask(t._id, -1)} title="Move up">
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline"
                        style={{ padding: '2px 8px', fontSize: '0.75rem' }}
                        disabled={busy || pos < 0 || pos >= sortedTasks.length - 1}
                        onClick={() => moveTask(t._id, 1)}
                        title="Move down"
                      >
                        ↓
                      </button>
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{t.title}</div>
                    {t.description && <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '4px' }}>{t.description}</div>}
                  </td>
                  {columns.priority && <td>{t.priority}</td>}
                  {columns.roleTag && <td>{ROLE_TAGS.find((r) => r.value === t.roleTag)?.label || t.roleTag}</td>}
                  {columns.assignee && <td>{t.assignee?.name || '—'}</td>}
                  {columns.dueDate && (
                    <td style={{ whiteSpace: 'nowrap' }}>{t.dueDate ? new Date(t.dueDate).toLocaleDateString() : '—'}</td>
                  )}
                  {columns.status && (
                    <td>
                      <select
                        className="form-select"
                        style={{ minWidth: '120px', fontSize: '0.8125rem' }}
                        value={t.status}
                        disabled={busy}
                        onChange={(e) => onUpdateTask(t._id, { status: e.target.value })}
                      >
                        {TASK_STATUSES.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </td>
                  )}
                  <td>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <button type="button" className="btn btn-outline" style={{ padding: '4px 10px', fontSize: '0.75rem' }} onClick={() => setEditTask({ ...t, assigneeId: t.assignee?._id || '' })}>
                        Edit
                      </button>
                      <button type="button" className="btn btn-outline" style={{ padding: '4px 10px', fontSize: '0.75rem' }} onClick={() => onDeleteTask(t._id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
        {!filteredTasks.length && <p style={{ color: 'var(--text-secondary)', marginTop: '12px' }}>No tasks match.</p>}
      </div>

      <Modal isOpen={showColSettings} onClose={() => setShowColSettings(false)} title="Visible columns">
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>Choose which columns to show in the task table. Preferences are saved in this browser.</p>
        <div style={{ display: 'grid', gap: '10px' }}>
          {Object.keys(defaultColumns()).map((key) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input type="checkbox" checked={columns[key]} onChange={() => toggleColumn(key)} />
              <span style={{ textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1').trim()}</span>
            </label>
          ))}
        </div>
        <button type="button" className="btn btn-primary" style={{ marginTop: '16px' }} onClick={() => setShowColSettings(false)}>
          Done
        </button>
      </Modal>

      <Modal isOpen={!!editTask} onClose={() => setEditTask(null)} title="Edit task" wide>
        {editTask && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onUpdateTask(editTask._id, {
                title: editTask.title,
                description: editTask.description,
                priority: editTask.priority,
                roleTag: editTask.roleTag,
                dueDate: editTask.dueDate || null,
                assignee: editTask.assigneeId || undefined
              });
            }}
            style={{ display: 'grid', gap: '12px' }}
          >
            <div className="form-group">
              <label className="form-label">Title</label>
              <input
                className="form-input"
                value={editTask.title}
                onChange={(e) => setEditTask((x) => ({ ...x, title: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-textarea"
                rows={3}
                value={editTask.description || ''}
                onChange={(e) => setEditTask((x) => ({ ...x, description: e.target.value }))}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label className="form-label">Assignee</label>
                <select
                  className="form-select"
                value={editTask.assigneeId ?? ''}
                onChange={(e) => setEditTask((x) => ({ ...x, assigneeId: e.target.value }))}
                >
                  <option value="">Unassigned</option>
                  {assigneeOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Due</label>
                <input
                  type="date"
                  className="form-input"
                  value={editTask.dueDate ? editTask.dueDate.slice(0, 10) : ''}
                  onChange={(e) => setEditTask((x) => ({ ...x, dueDate: e.target.value }))}
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select
                  className="form-select"
                  value={editTask.priority}
                  onChange={(e) => setEditTask((x) => ({ ...x, priority: e.target.value }))}
                >
                  {PRIORITIES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Work type</label>
                <select
                  className="form-select"
                  value={editTask.roleTag}
                  onChange={(e) => setEditTask((x) => ({ ...x, roleTag: e.target.value }))}
                >
                  {ROLE_TAGS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                Save
              </button>
              <button type="button" className="btn btn-outline" onClick={() => setEditTask(null)}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default LabTaskTracker;
